import { c as _c } from "react/compiler-runtime";
import { Button, Center, Heading, Spinner, Text, VStack } from '@chakra-ui/react';
import { advisorsBreadcrumbsWithTrail, useAdvisorsToolbar } from '@src/components/sco/Advisors/hooks/useAdvisorsToolbar';
import { AdvisorsSubNavLayout } from '@src/components/sco/Advisors/layout/AdvisorsSubNavLayout';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { useOwnSessionReviewSummary } from '@src/components/sco/Advisors/reviews/useOwnSessionReviewSummary';
import { isAdvisorProfileHubEligible } from '@src/components/sco/Advisors/utils/advisorProfileHub';
import { advisorProfileNavItems, AdvisorProfileSectionKey, isAdvisorProfileReviewsTabVisible } from '@src/components/sco/Advisors/utils/advisorProfileNav';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { useRouter } from 'next/router';
import { ReactNode, useEffect } from 'react';

/**
 * My Advisor Profile hub — Setup / Profile / Edit / Settings / Payouts / Reviews (once reviewed).
 * Only for submitted AdvisorProfile rows (not draft / missing).
 */
export const AdvisorProfileLayout = t0 => {
  const $ = _c(11);
  const {
    activeKey,
    title,
    description,
    action,
    compactHeader: t1,
    children
  } = t0;
  const compactHeader = t1 === undefined ? false : t1;
  const paths = useAdvisorsPaths();
  const ownReviews = useOwnSessionReviewSummary();
  const showReviewsTab = isAdvisorProfileReviewsTabVisible({
    hasReviews: ownReviews.hasReviews,
    hasLinkedClarity: ownReviews.hasLinkedClarity,
    activeKey
  });
  const navItems = advisorProfileNavItems(paths).filter(item => item.key !== "reviews" || showReviewsTab);
  let t2;
  if ($[0] !== activeKey) {
    t2 = item_0 => item_0.key === activeKey;
    $[0] = activeKey;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const activeItem = navItems.find(t2);
  useAdvisorsToolbar({
    breadcrumbs: activeKey === "setup" ? advisorsBreadcrumbsWithTrail({
      title: "My Advisor Profile",
      href: paths.ownProfile
    }) : advisorsBreadcrumbsWithTrail({
      title: "My Advisor Profile",
      href: paths.ownProfile
    }, {
      title: activeItem?.title ?? "Profile",
      href: activeItem?.href ?? paths.ownProfile
    })
  });
  const T0 = AdvisorsSubNavLayout;
  const t3 = "Setup, public card, editor, session rates, and payouts.";
  const t4 = "My Advisor Profile";
  const t5 = navItems.map(_temp);
  let t6;
  if ($[2] !== T0 || $[3] !== action || $[4] !== activeKey || $[5] !== children || $[6] !== compactHeader || $[7] !== description || $[8] !== t5 || $[9] !== title) {
    t6 = <T0 action={action} activeKey={activeKey} compactHeader={compactHeader} description={description} railDescription={t3} railTitle={t4} title={title} items={t5}>{children}</T0>;
    $[2] = T0;
    $[3] = action;
    $[4] = activeKey;
    $[5] = children;
    $[6] = compactHeader;
    $[7] = description;
    $[8] = t5;
    $[9] = title;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
};

/** Shared gate: hub is for submitted marketplace profiles only. */
export const AdvisorProfileHubGate = t0 => {
  const $ = _c(17);
  const {
    children
  } = t0;
  const paths = useAdvisorsPaths();
  const router = useRouter();
  const {
    currentUser,
    currentUserLoading
  } = useCurrentUser();
  const t1 = currentUser?.advisorProfile?.statusKey;
  let t2;
  if ($[0] !== t1) {
    t2 = isAdvisorProfileHubEligible(t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const eligible = t2;
  let t3;
  let t4;
  if ($[2] !== currentUser || $[3] !== currentUserLoading || $[4] !== eligible || $[5] !== paths.apply || $[6] !== router) {
    t3 = () => {
      if (currentUserLoading || !currentUser) {
        return;
      }
      if (!eligible) {
        router.replace(paths.apply);
      }
    };
    t4 = [currentUser, currentUserLoading, eligible, paths.apply, router];
    $[2] = currentUser;
    $[3] = currentUserLoading;
    $[4] = eligible;
    $[5] = paths.apply;
    $[6] = router;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  useEffect(t3, t4);
  if (currentUserLoading) {
    let t5;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <Center height="full" padding={8}><Spinner size="xl" /></Center>;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    return t5;
  }
  if (!currentUser) {
    let t5;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <Center height="full" padding={8}><Text>Sign in to manage your advisor profile.</Text></Center>;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    return t5;
  }
  if (!eligible) {
    let t5;
    let t6;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <Heading size="lg">Apply to advise first</Heading>;
      t6 = <Text color="colorPalette.fg/60" fontSize="sm">Finish the application to unlock setup, your public card, and settings.</Text>;
      $[11] = t5;
      $[12] = t6;
    } else {
      t5 = $[11];
      t6 = $[12];
    }
    let t7;
    if ($[13] !== paths.apply) {
      t7 = <Center height="full" padding={8}><VStack gap={3} maxWidth="md" textAlign="center">{t5}{t6}<Button asChild={true} variant="cta"><NextLink href={paths.apply}>Apply to advise</NextLink></Button></VStack></Center>;
      $[13] = paths.apply;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    return t7;
  }
  let t5;
  if ($[15] !== children) {
    t5 = <>{children}</>;
    $[15] = children;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return t5;
};
function _temp(item_1) {
  return {
    key: item_1.key,
    title: item_1.title,
    href: item_1.href,
    icon: item_1.icon
  };
}